import ConfirmDialog from '@/components/ConfirmDialog';
import RadioButtonOutline from '@/components/RadioButtonOutline';
import Upload from '@/components/Upload';
import Constants from '@/Constants';
import { FileUploadData } from '@/hooks/upload/FileUpload';
import Option from '@/models/Option';
import { AnswerOption, Question, QuestionCreateData, QuestionType } from '@/models/QuestionCreateData';
import { ApplicationState } from '@/store';
import {
    deleteQuestionRequest,
    postQuestionRenewClear,
    postQuestionRenewRequest,
    postQuestionRequest,
    putQuestionRequest,
} from '@/store/evaluationQuestion/actions';
import { getLessonRequest } from '@/store/lesson/action';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/esm/Modal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import EditAlternative from './EditAlternative';
import './style.scss';

interface PropsType {
    show: boolean;
    setShow: (value: boolean) => void;
    setSaveAndClose: (value: boolean) => void;
}

type ConfirmationType = { content: string; handleYes: () => void; handleNo: () => void };

const QuestionModal = ({ show, setShow, setSaveAndClose }: PropsType) => {
    const { strings, lesson, evaluation, isNew, editionQuestion, success, questions, course } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        evaluation: state.lessons.evaluation,
        lesson: state.lessons.currentData,
        isNew: state.evaluationQuestion.isNew,
        editionQuestion: state.evaluationQuestion.data as Question,
        success: state.evaluationQuestion.success,
        questions: state.evaluationQuestion.list.questions,
        course: state.lists.courseData,
    }));

    const uploadImagePathRef = useRef<{ clearFileInput: Function; local: string }>(null);

    const dispatch = useDispatch();

    const initState = {
        questionTitle: '',
        questionImagePath: undefined,
        options: [
            { isCorrect: false, description: '', answerOptionOrder: 0 },
            { isCorrect: false, description: '', answerOptionOrder: 1 },
            { isCorrect: false, description: '', answerOptionOrder: 2 },
        ],
    };

    const [edition, setEdition] = useState(false);
    const [confirmation, setConfirmation] = useState(false);
    const [questionTitle, setQuestionTitle] = useState(initState.questionTitle);
    const [options, setOptions] = useState<AnswerOption[]>(initState.options);
    const [questionData, setQuestionData] = useState<QuestionCreateData>();
    const [questionType, setQuestionType] = useState<QuestionType>('SINGLE_OPTION');
    const [questionImagePath, setQuestionImagePath] = useState<string>();
    const [localQuestionImage, setLocalQuestionImage] = useState<string>();

    const questionTypeOptions: Option[] = useMemo(
        () => [
            { key: 'SINGLE_OPTION', value: strings.ONE_CORRECT_ANSWER },
            { key: 'MULTI_OPTION', value: strings.MULTIPLE_CORRECT_ANSWERS },
        ],
        [strings.ONE_CORRECT_ANSWER, strings.MULTIPLE_CORRECT_ANSWERS],
    );

    const isSingleOption: boolean = questionType === 'SINGLE_OPTION';

    const hasCorrectOptions: boolean = options.some(({ isCorrect }) => isCorrect);

    const isAllCorrectOptionsSelected: boolean = options.every(({ isCorrect }) => isCorrect);

    useEffect(() => {
        if (editionQuestion && editionQuestion.id) {
            setEdition(true);
            setQuestionTitle(editionQuestion.description);
            setOptions(editionQuestion.options);
            setQuestionType(editionQuestion.type);
            setQuestionImagePath(editionQuestion.imagePath);
        } else {
            cleanState();
        }
    }, [editionQuestion]);

    const [confirmationType, setConfirmationType] = useState<ConfirmationType>({
        content: '',
        handleYes: () => {},
        handleNo: () => {
            setConfirmation(false);
            setShow(true);
        },
    });

    const handleChecking = (item: AnswerOption, index: number, checked: boolean) => {
        let editedoptions = [...options];

        if (isSingleOption) {
            editedoptions = options.map((item, idx) => ({ ...item, isCorrect: idx === index }));
        } else {
            editedoptions = options.map((item, idx) => (idx === index ? { ...item, isCorrect: checked } : { ...item }));
        }

        setOptions([...editedoptions]);
    };

    const handleChange = (item: AnswerOption, index: number, description: string) => {
        options[index] = { ...item, description };
        setOptions([...options]);
    };

    const addAlternative = () => {
        setOptions([...options, { description: '', isCorrect: false, answerOptionOrder: options.length + 1 }]);
    };

    const handleDelete = (index: number) => {
        if (options.length < 3) {
            toast.info(strings.DELETE_ALTERNATIVE_MESSAGE);
            return;
        }
        options.splice(index, 1);
        setOptions([...options]);
    };

    const handleCloseConfirmation = () => {
        setConfirmationType({
            content: strings.QUESTION_RETURN_MESSAGE,
            handleYes() {
                setLocalQuestionImage(undefined);
                cleanState();
                setConfirmation(false);
            },
            handleNo: confirmationType.handleNo,
        });
        setShow(false);
        setConfirmation(true);
    };

    const handleDeleteConfirmation = () => {
        setConfirmationType({
            content: strings.DELETE_QUESTION_MESSAGE,
            handleYes: () => {
                if (questionData && questionData.question.id) dispatch(deleteQuestionRequest(questionData.question.id, questionData.lessonId));
                confirmationType.handleNo();
                setQuestionImagePath(undefined);
                setLocalQuestionImage(undefined);
            },
            handleNo: confirmationType.handleNo,
        });
        setShow(false);
        setConfirmation(true);
    };

    const validateQuestion = () => {
        const titleEmpty = questionTitle === '' || ['<p><br></p>', '<p></p>'].includes(questionTitle);
        showError(titleEmpty, strings.QUESTION_STATEMENT);

        const hasBlankoptions = options.filter((item) => item.description.trim() === '').length > 0;
        showError(hasBlankoptions, strings.BLANK_ALTERNATIVES);

        const hasNoAnswer = options.filter((item) => item.isCorrect).length === 0;
        showError(hasNoAnswer, strings.SELECT_CORRECT_ALTERNATIVE);

        return !titleEmpty && !hasBlankoptions && !hasNoAnswer;
    };

    const generateBizId = () => {
        const lessonId = lesson ? lesson.id : 0;
        if (!questions || questions.length === 0) {
            return `q1-${lessonId}`;
        } else {
            return `q${Math.max.apply(
                Math,
                questions.map(function(q) {
                    return q.id ? q.id : 0;
                }),
            )}-${lessonId}`;
        }
    };

    useEffect(() => {
        if (lesson && evaluation) {
            setQuestionData({
                lessonId: lesson ? lesson.id : 0,
                question: {
                    id: editionQuestion ? editionQuestion.id : null,
                    description: questionTitle,
                    isActive: true,
                    type: questionType,
                    options,
                    bizId: generateBizId(),
                    questionOrder: questions ? questions.length : 0,
                    imagePath: questionImagePath,
                },
                topic: evaluation.topics && evaluation.topics.length ? evaluation.topics[0] : {},
            } as QuestionCreateData);
        }
    }, [evaluation, lesson, questionTitle, options, questionType, questionImagePath]);

    useEffect(() => {
        if (success) {
            if (lesson && lesson.id) {
                dispatch(getLessonRequest(lesson.id, false, true));
            }
            if (isNew) {
                cleanState();
                dispatch(postQuestionRenewClear());
                setEdition(false);
            }
        }
    }, [isNew, success]);

    const cleanState = () => {
        uploadImagePathRef?.current?.clearFileInput();
        setQuestionImagePath(initState.questionImagePath);
        setLocalQuestionImage(undefined);
        setEdition(false);
        setQuestionTitle(initState.questionTitle);
        setOptions(initState.options);
    };

    const saveAndNewQuestion = () => {
        if (!validateQuestion() || !questionData) return;
        dispatch(postQuestionRenewRequest(questionData));
    };

    const saveQuestion = () => {
        if (!validateQuestion() || !questionData) return;
        dispatch(postQuestionRequest(questionData));
        setShow(false);
        setSaveAndClose(true);
    };

    const saveEditions = () => {
        if (!validateQuestion() || !questionData) return;
        dispatch(putQuestionRequest(questionData.question));
        setShow(false);
        cleanState();
    };

    const showError = (validation: boolean, msg: string) => {
        if (validation) {
            toast.error(msg);
        }
    };

    const clearCorrectOptions = (): void => {
        setOptions(options.map((option: AnswerOption) => ({ ...option, isCorrect: false })));
    };

    const selectQuestionType = ({ key }: Option): void => {
        clearCorrectOptions();
        setQuestionType(key as QuestionType);
    };

    return (
        <>
            <Modal className="question-dialog" show={show}>
                <Modal.Body>
                    <div className="question-modal">
                        <div className="question-modal__title">
                            <span> {edition ? strings.EDIT_QUESTION : strings.ADD_QUESTION}</span>
                            <i className="material-icons-outlined close-icon" onClick={handleCloseConfirmation}>
                                {Constants.icons.CLOSE}
                            </i>
                        </div>
                        <div className="question-modal__input">
                            <span className="enunciated">{strings.CONTEXT_QUESTION}</span>
                            <ReactQuill
                                formats={Constants.reactQuillAvailableFormats}
                                onChange={(value) => setQuestionTitle(value)}
                                placeholder={strings.WRITE_CONTEXT_HERE}
                                theme="snow"
                                value={questionTitle}
                            />
                        </div>

                        <div className="question-modal__upload-image">
                            <h1 className="warning">{strings.IMAGE}</h1>

                            <Upload
                                onComplete={(response: FileUploadData) => {
                                    setQuestionImagePath(response.key);
                                    setLocalQuestionImage(uploadImagePathRef?.current?.local);
                                }}
                                onRemove={() => {
                                    setQuestionImagePath(undefined);
                                    setLocalQuestionImage(undefined);
                                }}
                                previewFile={{ uri: localQuestionImage || questionImagePath, name: questionImagePath }}
                                ref={uploadImagePathRef}
                                typeAccepted="IMAGE"
                            />
                        </div>

                        <div className="question-type-options">
                            <span className="enunciated">{strings.SELECT_MULTIPLE_CHOICE_QUESTION_TYPE}</span>
                            <RadioButtonOutline
                                disabled={course.status === 'PUBLISHED'}
                                onSelectOption={selectQuestionType}
                                options={questionTypeOptions}
                                selectedOptionKey={questionType}
                            />
                        </div>

                        <span className="question-modal__alternative-title">
                            {isSingleOption
                                ? strings.ADD_THE_ALTERNATIVES_AND_SELECT_THE_CORRECT_ONE
                                : strings.ADD_THE_ALTERNATIVES_AND_SELECT_THE_CORRECT_ONES}
                        </span>

                        <div className="question-modal__options">
                            {options.map((item, index) => (
                                <EditAlternative
                                    handleChange={(value) => handleChange(item, index, value)}
                                    item={item}
                                    key={`af-question-${index}`}
                                    onChecking={(checked) => handleChecking(item, index, checked)}
                                    onDelete={() => handleDelete(index)}
                                />
                            ))}
                        </div>

                        {isAllCorrectOptionsSelected && (
                            <div className="question-alert">
                                <i className="material-icons icon">{Constants.icons.REPORT_PROBLEM}</i>
                                <span dangerouslySetInnerHTML={{ __html: strings.YOU_HAVE_SELECTED_ALL_ALTERNATIVES }}></span>
                            </div>
                        )}

                        <div className="question-modal__add">
                            <i className="material-icons" onClick={addAlternative}>
                                {Constants.icons.ADD}
                            </i>
                            <span onClick={addAlternative}>{strings.ADD_ALTERNATIVE}</span>
                        </div>
                    </div>

                    <div className="question-modal__controls">
                        {edition ? (
                            <>
                                <button className="btn delete" onClick={handleDeleteConfirmation}>
                                    {strings.DELETE}
                                </button>

                                <button className="btn btn-primary" disabled={!hasCorrectOptions} onClick={saveEditions}>
                                    {strings.SAVE_EDITIONS}
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="btn btn-light" disabled={!hasCorrectOptions} onClick={saveQuestion}>
                                    {strings.SAVE}
                                </button>

                                <button className="btn btn-primary" disabled={!hasCorrectOptions} onClick={saveAndNewQuestion}>
                                    {strings.SAVE_ADD_QUESTION}
                                </button>
                            </>
                        )}
                    </div>
                </Modal.Body>
            </Modal>

            <ConfirmDialog
                content={confirmationType.content}
                leftClick={confirmationType.handleYes}
                rightClick={confirmationType.handleNo}
                show={confirmation}
            />
        </>
    );
};

export default QuestionModal;
